import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { useTranslation } from 'react-i18next';
import API from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import generateTxnRef from '../utils/generateTxnRef';
import loadRazorpayScript from '../utils/loadRazorpayScript';

const AMOUNTS = [501, 1100, 2100, 5100, 11000, 21000];
const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || '';

export default function Donations() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [settings, setSettings] = useState({});
  const [amount, setAmount] = useState('');
  const [customAmount, setCustomAmount] = useState('');
  const [purpose, setPurpose] = useState('general');
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [mobile, setMobile] = useState(user?.mobile || '');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [txnRef, setTxnRef] = useState(generateTxnRef);

  const PURPOSES = [
    { value: 'general', label: t('donations.purposeGeneral') },
    { value: 'fodder', label: t('donations.purposeFodder') },
    { value: 'medical', label: t('donations.purposeMedical') },
    { value: 'cow_adoption', label: t('donations.purposeCowAdoption') },
    { value: 'infrastructure', label: t('donations.purposeInfrastructure') },
    { value: 'festival', label: t('donations.purposeFestival') },
  ];

  useEffect(() => {
    API.get('/settings').then(r => setSettings(r.data)).catch(() => {});
    if (user) { setName(user.name || ''); setEmail(user.email || ''); setMobile(user.mobile || ''); }
  }, [user]);
  const logoImageUrl = settings.logoImage ? (settings.logoImage.startsWith('http') ? settings.logoImage : `${API_BASE}${settings.logoImage}`) : null;

  const selectedAmount = customAmount ? parseFloat(customAmount) : parseFloat(amount);

  // Regenerate the QR's transaction reference whenever the amount changes,
  // so a screenshotted QR can't silently be reused for a different donation
  useEffect(() => { setTxnRef(generateTxnRef()); }, [amount, customAmount]);

  const handleDonate = async () => {
    if (!selectedAmount || selectedAmount < 1) return toast.error(t('donations.toastSelectAmount'));
    if (!name.trim()) return toast.error(t('donations.toastNameRequired'));
    if (!email.trim() || !email.includes('@')) return toast.error(t('donations.toastValidEmail'));

    setLoading(true);
    try {
      const res = await API.post('/donations/initiate', {
        amount: selectedAmount,
        purpose,
        name,
        email,
        mobile,
        isAnonymous,
        message,
        userId: user?._id,
      });
      const order = res.data;

      await loadRazorpayScript();

      const rzp = new window.Razorpay({
        key: order.keyId,
        amount: order.amount,
        currency: order.currency,
        order_id: order.orderId,
        name: settings.siteName || 'Goshala Donation',
        description: PURPOSES.find(p => p.value === purpose)?.label || purpose,
        prefill: { name: order.name, email: order.email, contact: order.mobile },
        notes: { purpose },
        theme: { color: '#FF9933' },
        handler: async (response) => {
          try {
            await API.post('/donations/verify', {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            navigate(`/donation/success?txnid=${order.txnId}`);
          } catch (err) {
            toast.error(err.response?.data?.message || t('donations.toastPaymentFailed'));
            navigate('/donation/failure');
          }
        },
        modal: {
          ondismiss: () => {
            setLoading(false);
            toast.info(t('donations.toastPaymentCancelled'));
          },
        },
      });

      rzp.on('payment.failed', (response) => {
        setLoading(false);
        toast.error(response.error?.description || t('donations.toastPaymentFailed'));
      });

      rzp.open();
      setLoading(false);
    } catch (err) {
      toast.error(err.response?.data?.message || t('donations.toastPaymentFailed'));
      setLoading(false);
    }
  };

  const upiId = settings.upiId || '73966431056m@pnb';
  const upiPaymentLink = [
    `upi://pay`,
    `?pa=${encodeURIComponent(upiId)}`,
    `&pn=${encodeURIComponent(settings.siteName || 'Goshala')}`,
    `&cu=INR`,
    `&tr=${encodeURIComponent(txnRef)}`,
    selectedAmount ? `&am=${selectedAmount}` : '',
  ].join('');
  const qrCodeImageUrl = settings.qrCodeImage ? (settings.qrCodeImage.startsWith('http') ? settings.qrCodeImage : `${API_BASE}${settings.qrCodeImage}`) : null;

  return (
    <div>
      <div className="page-header">
        <h1>{t('donations.pageTitle')}</h1>
        <p>{t('donations.pageSubtitle')}</p>
      </div>

      <div className="donation-page">
        <div className="donation-grid">
          {/* FORM */}
          <div className="donation-form-card">
            <h2 style={{ fontWeight: 700, fontSize: '1.4rem', marginBottom: 20 }}>{t('donations.makeADonation')}</h2>

            {/* Amount Presets */}
            <div className="form-group">
              <label className="form-label">{t('donations.selectAmount')}</label>
              <div className="amount-presets">
                {AMOUNTS.map(a => (
                  <button key={a} className={`amount-preset${amount === String(a) && !customAmount ? ' active' : ''}`}
                    onClick={() => { setAmount(String(a)); setCustomAmount(''); }}>
                    ₹{a.toLocaleString('en-IN')}
                  </button>
                ))}
              </div>
              <input className="form-input" type="number" placeholder={t('donations.customAmountPlaceholder')}
                value={customAmount} onChange={e => { setCustomAmount(e.target.value); setAmount(''); }} min="1" />
            </div>

            <div className="form-group">
              <label className="form-label">{t('donations.purpose')}</label>
              <select className="form-select" value={purpose} onChange={e => setPurpose(e.target.value)}>
                {PURPOSES.map(p => <option key={p.value} value={p.value}>{p.value == "cow_adoption" && logoImageUrl
                ? <img src={logoImageUrl} alt={settings.siteName} className="footer-brand-logo" />
                : ''
              }{p.label}</option>)}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">{t('donations.fullName')}</label>
              <input className="form-input" placeholder={t('donations.namePlaceholder')} value={name} onChange={e => setName(e.target.value)} />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{t('donations.email')}</label>
                <input className="form-input" type="email" placeholder={t('donations.emailPlaceholder')} value={email} onChange={e => setEmail(e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">{t('donations.mobile')}</label>
                <input className="form-input" type="tel" placeholder={t('donations.mobilePlaceholder')} value={mobile} onChange={e => setMobile(e.target.value)} />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">{t('donations.message')}</label>
              <textarea className="form-input" rows={3} placeholder={t('donations.messagePlaceholder')} value={message} onChange={e => setMessage(e.target.value)} />
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
              <input type="checkbox" id="anon" checked={isAnonymous} onChange={e => setIsAnonymous(e.target.checked)} style={{ width: 18, height: 18, cursor: 'pointer' }} />
              <label htmlFor="anon" style={{ fontSize: '0.9rem', color: '#555', cursor: 'pointer' }}>{t('donations.anonymous')}</label>
            </div>

            <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', padding: '14px', fontSize: '1.1rem' }}
              onClick={handleDonate} disabled={loading}>
              {loading ? t('donations.processing') : t('donations.donateButton', { amount: selectedAmount ? selectedAmount.toLocaleString('en-IN') : '...' })}
            </button>

            <p style={{ color: '#888', fontSize: '0.82rem', textAlign: 'center', marginTop: 12 }}>
              {t('donations.securedNote')}
            </p>
          </div>

          {/* SIDEBAR */}
          <div className="donation-sidebar">
            {/* QR Code */}
            <div className="donation-sidebar-card" style={{ marginBottom: 20 }}>
              <h3 style={{ fontWeight: 700, marginBottom: 16, color: '#1a1a1a' }}>{t('donations.qrSidebarTitle')}</h3>
              <div className="qr-box">
                <h4>{t('donations.scanToDonate')}</h4>
                <p>{settings.donationQrText || t('donations.scanWithAnyUpiApp')}</p>
                <div style={{ display: 'flex', justifyContent: 'center', margin: '16px 0' }}>
                  {qrCodeImageUrl
                    ? <img src={qrCodeImageUrl} alt="Donation QR code" style={{ width: 180, height: 180, objectFit: 'contain' }} />
                    : <QRCodeSVG value={upiPaymentLink} size={180} fgColor="#000000" bgColor="#ffffff" level="M" />
                  }
                </div>
                <div className="upi-id">{settings.upiId || '73966431056m@pnb'}</div>
                <p style={{ marginTop: 8, fontSize: '0.78rem', color: '#aaa' }}>{t('donations.upiApps')}</p>
                {!qrCodeImageUrl && (
                  <>
                    <button type="button" onClick={() => setTxnRef(generateTxnRef())}
                      style={{ marginTop: 14, background: 'none', border: '1.5px solid var(--border)', borderRadius: 8, padding: '6px 14px', color: 'var(--text-mid)', fontSize: '0.8rem', cursor: 'pointer' }}>
                      {t('donations.generateNewQr')}
                    </button>
                    <p style={{ marginTop: 6, fontSize: '0.72rem', color: '#bbb' }}>{t('donations.refLabel', { ref: txnRef })}</p>
                  </>
                )}
              </div>

              {/* Bank Transfer — shown along with the QR code, in the same card */}
              {settings.bankAccount && (
                <div style={{ marginTop: 18, paddingTop: 18, borderTop: '1px dashed var(--border)' }}>
                  <h4 style={{ fontWeight: 700, marginBottom: 10, color: '#1a1a1a', fontSize: '0.95rem' }}>{t('donations.bankTransferTitle')}</h4>
                  <div style={{ fontSize: '0.88rem', lineHeight: 1.9 }}>
                    <div><strong>{t('donations.bankLabel')}</strong> {settings.bankName}</div>
                    <div><strong>{t('donations.accountLabel')}</strong> {settings.bankAccount}</div>
                    <div><strong>{t('donations.ifscLabel')}</strong> {settings.bankIFSC}</div>
                    <div><strong>{t('donations.branchLabel')}</strong> {settings.bankBranch}</div>
                  </div>
                </div>
              )}
            </div>

            {/* Tax info */}
            <div className="donation-sidebar-card" style={{ background: 'var(--green-light)', border: '1px solid rgba(19,136,8,0.3)' }}>
              <h4 style={{ color: 'var(--green)', marginBottom: 8 }}>{t('donations.taxExemptionTitle')}</h4>
              <p style={{ fontSize: '0.85rem', color: '#444', lineHeight: 1.6 }}>
                {t('donations.taxExemptionText')}
                {settings.section80GNo && <><br /><strong>{t('donations.section80GNo')}</strong> {settings.section80GNo}</>}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
